import { useEffect, useRef, useState } from "react";
import { getClassForState, getFocusFilled } from "./utils";
import COUNTRIES from "../data/js/all-countries";

export const FormPopup = ({
	single: _single = undefined,
	activeItem: _activeItem = null,
	filled,
	state,
	focus,
	setFilled,
	setErrors: _setErrors,
	setState,
	className = "",
	placeholder: _placeholder = "",
	label,
	id,
	onBlur: _onBlur,
	onFocus,
	onChange: _onChange,
	errorMessages,
	errors,
	popupContents,
	heading: _heading,
	setPopupOpen,
	setPopupContents,
	realName,
	popupOpen: _popupOpen,
	validate: _validate,
}) => {
	// Provide safe defaults for SSR
	const safeState = state || {};
	const postFields = safeState;

	className = `${getFocusFilled(id, focus, postFields)} ${getClassForState(
		id,
		errors[id],
	)} ${className}`;
	return (
		<div className="form-popup">
			<label
				className={`input-label input-label-${id} ${className}`}
				htmlFor={`${id}-input`}
			>
				<span className={`input-placeholder ${id}-label`}>{label}</span>
				{realName ? (
					<input
						type="hidden"
						name={`${realName}`}
						id={`${realName}`}
						value={postFields[realName] || ""}
					/>
				) : (
					""
				)}
				<input
					data-testid={`${id}-input`}
					type="text"
					id={`${id}-input`}
					name={`${id}-input`}
					readOnly={true}
					autoComplete="off"
					className="form-input bordered"
					value={postFields[id] || ""}
					onBlur={(e) => {
						const fill = { ...filled };
						fill[id] = e?.target?.value?.trim() !== "";
						setFilled(fill);
					}}
					onFocus={(e) => {
						e.preventDefault();
						const f = { ...focus };
						f[id] = true;
						onFocus(f);
						setPopupContents(popupContents);
						setPopupOpen(true);
					}}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						const spd = { ...postFields };
						spd[id] = e.target.value;
						setState(spd);
						setPopupContents(popupContents);
					}}
				/>
				<div className="valid-check" />
				{errorMessages}
			</label>
		</div>
	);
};

export const PriceInput = ({
	single: _single = undefined,
	state,
	focus,
	filled,
	setFilled,
	setErrors: _setErrors,
	setState,
	className = "",
	label: _label,
	id,
	onFocus,
	errorMessages,
	errors,
	validate,
}) => {
	// Provide safe defaults for SSR
	const safeState = state || {};
	const postFields = safeState;

	className = `${getFocusFilled(id, focus, postFields)} ${getClassForState(
		id,
		errors[id],
	)} ${className}`;

	// Validate on mount and when price is set from localStorage or edit
	useEffect(() => {
		if (postFields[id] && parseFloat(postFields[id]) > 0 && typeof validate === 'function') {
			const timer = setTimeout(() => {
				validate();
			}, 100);
			return () => clearTimeout(timer);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postFields[id], id]);

	// Shown as typed, cents included, behind a "$". (A Currency()-formatted
	// display sat behind a constant `USE_SOME_CENTS = true` and never ran.)
	// A missing price used to show as "$undefined".
	const price = postFields[id] ?? "";
	const val = `${price}`.length > 0 ? `$${price}` : "";

	return (
		<label
			className={`price-input-label input-label-${id} ${className}`}
			htmlFor={`${id}-input`}
		>
			<input
				data-testid={`${id}-input`}
				type="text"
				id={`${id}-input`}
				name={`${id}-input`}
				className="price-input"
				inputMode="decimal"
				value={val}
				onBlur={(_e) => {
					const fill = { ...filled };
					fill[id] = _e?.target?.value?.trim() !== "";
					setFilled(fill);
					const f = { ...focus };
					f[id] = false;
					onFocus(f);
					validate();
				}}
				onFocus={(_e) => {
					const f = { ...focus };
					f[id] = true;
					onFocus(f);
				}}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					let value = e.target.value.replace(/[^0-9.]/g, "");
					const decimalIndex = value.indexOf(".");
					if (
						decimalIndex !== -1 &&
						decimalIndex < value.length - 3
					) {
						value = value.slice(0, decimalIndex + 3);
					}

					setState((prevState: Record<string, unknown>) => ({
						...prevState,
						[id]: value
					}));
				}}
			/>
			<div className="valid-check" />
			{errorMessages}
		</label>
	);
};

export const FormInput = ({
	single: _single = undefined,
	state,
	focus,
	filled,
	setFilled,
	setErrors,
	setState,
	className = "",
	inputClassName = "",
	label,
	id,
	onBlur = undefined,
	onInput = undefined,
	validate,
	onFocus,
	onChange: _onChange = undefined,
	errorMessages,
	errors,
	realName: _realName = "",
	inputMode = "text",
	inputType = "text",
	placeholder = "",
	min,
	autocomplete = "on",
	readOnly = false,
	popupOpen: _popupOpen = false,
	setPopupOpen: _setPopupOpen = undefined,
	popupContents: _popupContents = null,
	heading: _heading = "",
	setPopupContents: _setPopupContents = undefined,
}) => {
	// Provide safe defaults for SSR
	const safeState = state || {};
	const postFields = safeState;
	const [dirty, setDirty] = useState(false);
	className = `${getFocusFilled(id, focus, postFields)} ${getClassForState(
		id,
		errors[id],
	)} ${className}`;

	return (
		<label
			className={`input-label input-label-${id} ${className}`}
			htmlFor={`${id}-input`}
		>
			<span className={`input-placeholder ${id}-label`}>{label}</span>
			<input
				data-testid={`${id}-input`}
				type={inputType}
				id={`${id}-input`}
				name={`${id}-input`}
				className={`form-input bordered ${inputClassName}`}
				inputMode={inputMode as React.HTMLAttributes<HTMLInputElement>["inputMode"]}
				readOnly={readOnly}
				min={min}
				placeholder={placeholder}
				autoComplete={autocomplete}
				value={postFields[id] || ""}
				onBlur={(e) => {
					const fill = { ...filled };
					fill[id] = e?.target?.value?.trim() !== "";
					setFilled(fill);
					const f = { ...focus };
					f[id] = false;
					if (validate) validate(postFields, errors, setErrors);
					if (onBlur && dirty) {
						setDirty(false);
						onBlur(postFields);
					}
				}}
				onFocus={(_e) => {
					const f = { ...focus };
					f[id] = true;
					if (onFocus) onFocus(f);
				}}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					const newValue = e.target.value;

					if (setState) {
						setState((prevState: Record<string, unknown>) => {
							if (prevState[id] !== newValue) {
								setDirty(true);
								return {
									...prevState,
									[id]: newValue
								};
							}
							return prevState;
						});
					}
				}}
				onInput={(e) => {
					if (onInput) onInput((e.target as HTMLInputElement).value);
				}}
			/>
			<div className="valid-check" />
			{errorMessages}
		</label>
	);
};

export const FormSelect = ({
	single: _single = undefined,
	options,
	state,
	focus,
	filled,
	setFilled,
	setErrors,
	setState,
	className = "",
	label,
	id,
	onBlur: _onBlur,
	validate,
	onFocus,
	onChange: _onChange,
	errorMessages,
	errors,
	realName: _realName,
	def = 0,
	// The browser's autofill name, e.g. `shipping address-level1`. Without it
	// the browser filled the street, city and ZIP of a checkout address and
	// left State empty (owner, 2026-11-03).
	autocomplete = undefined as string | undefined,
}) => {
	// Provide safe defaults for SSR
	const safeState = state || {};
	const postFields = safeState;
	// An updater, not `{...state}`: autofill sets several fields in one burst,
	// and a copy of the state from the last render wrote the others back out.
	const write = (value: string) => {
		if (setState) setState((prev: Record<string, string>) => ({ ...prev, [id]: value }));
	};

	className = `${getFocusFilled(id, focus, postFields)} ${getClassForState(
		id,
		errors[id],
	)} ${className}`;
	return (
		<label
			className={`input-label input-label-${id} ${className}`}
			htmlFor={`${id}-input`}
		>
			<span className={`input-placeholder ${id}-label`}>{label}</span>
			<select
				data-testid={`${id}-input`}
				id={`${id}-input`}
				name={`${id}-input`}
				className="form-select bordered"
				autoComplete={autocomplete}
				value={postFields[id] || ""}
				onBlur={(e) => {
					const fill = { ...filled };
					fill[id] = e?.target?.value?.trim() !== "";
					setFilled(fill);
					const f = { ...focus };
					f[id] = false;
					onFocus(f);
					if (validate) validate(postFields, errors, setErrors);
				}}
				onFocus={(_e) => {
					const f = { ...focus };
					f[id] = true;
					onFocus(f);
				}}
				onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
					if (validate) validate(postFields, errors, setErrors);
					write(e.target.value);
				}}
				onInput={(e: React.FormEvent<HTMLSelectElement>) => {
					if (validate) validate(postFields, errors, setErrors);
					write((e.target as HTMLSelectElement).value);
				}}
			>
				{def === 0 ? <option key="def" value="" /> : ""}
				{options.map((opt: { id: string; name: string }, i: number) => (
					<option key={opt.id || i} value={opt.id}>
						{opt.name}
					</option>
				))}
			</select>
			<div className="valid-check" />
			{errorMessages}
		</label>
	);
};

export const FormTextArea = ({
	single: _single = undefined,
	state,
	focus,
	filled,
	setFilled,
	setErrors,
	setState,
	className,
	label,
	id,
	validate,
	onFocus,
	onChange: _onChange,
	errorMessages,
	errors,
}) => {
	// Provide safe defaults for SSR
	const safeState = state || {};
	const postFields = safeState;

	className = `${getFocusFilled(id, focus, postFields)} ${getClassForState(
		id,
		errors[id],
	)} ${className}`;

	const resize = (target: HTMLTextAreaElement) => {
		const offset = target.offsetHeight - target.clientHeight;
		target.style.height = "auto";
		const tSH = target.scrollHeight;
		const ch = target.value.length;
		const oneLine = ch < 40;
		const scrollHeight = oneLine && tSH > 40 && tSH < 70 ? 40 : tSH;
		target.style.height = Math.max(scrollHeight + offset, 40) + "px";
	};

	const textareaRef = useRef<HTMLTextAreaElement>(null);

	// Effects run after the commit that attached the ref, so it is always set.
	useEffect(() => {
		resize(textareaRef.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postFields[id]]);

	return (
		<label
			className={`input-label input-label-${id} ${className}`}
			htmlFor={`${id}-input`}
		>
			<span className={`input-placeholder ${id}-label`}>{label}</span>
			<textarea
				ref={textareaRef}
				data-testid={`${id}-input`}
				id={`${id}-input`}
				name={`${id}-input`}
				className="form-input bordered"
				value={postFields[id] || ""}
				onBlur={(_e) => {
					const fill = { ...filled };
					fill[id] = true;
					setFilled(fill);
					const f = { ...focus };
					f[id] = false;
					onFocus(f);
					if (validate) validate(postFields, errors, setErrors);
				}}
				onFocus={(_e) => {
					const f = { ...focus };
					f[id] = true;
					onFocus(f);
				}}
				onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
					const newValue = e.target.value;

					setState((prevState: Record<string, unknown>) => ({
						...prevState,
						[id]: newValue
					}));
				}}
				onInput={(e) => {
					resize(e.target as HTMLTextAreaElement);
				}}
			/>
			<div className="valid-check" />
			{errorMessages}
		</label>
	);
};

// Several countries share a dial code (e.g. +44 is also Guernsey/Jersey/IoM,
// +1 is also Canada + Caribbean); show the primary country's flag for those.
const DIAL_CODE_PRIMARY: Record<string, string> = {
	"1": "US",
	"7": "RU",
	"44": "GB",
	"61": "AU",
};

// The flag only mirrors the dial code typed into the phone box. It used to
// carry a click handler that opened a country picker (AllFlags), but the handler
// returned before opening it, so the picker could never be reached and is gone.
const CountryCode = ({ state }: Record<string, unknown>) => {
	const { countryCode } = state as Record<string, unknown>;
	return (
		<div
			className="country-chooser"
			data-testid="country-chooser"
		>
			<div className="choosen-country flags">
				<div className="choosen">
					<div
						className={`flag flag-${(countryCode as Record<string, string>)?.code?.toLowerCase()}`}
					/>
				</div>
			</div>
			<div className="right-border" />
		</div>
	);
};

export const PhoneInput = ({
	state,
	focus,
	filled: _filled,
	setFilled: _setFilled,
	setFocus,
	setErrors: _setErrors,
	setState,
	className = "",
	inputClassName = "",
	label,
	realName = "phone",
	id = "phone",
	onFocus: _onFocus,
	errorMessages,
	errors,
	phoneLabel,
	validate,
	setPopupContents: _setPopupContents,
	setPopupOpen: _setPopupOpen,
}) => {
	// Provide safe defaults for SSR
	const safeState = state || {};
	const postFields = safeState;

	className = `${getFocusFilled(id, focus, postFields)} ${getClassForState(
		id,
		errors[id],
	)} ${className}`;
	return (
		<label
			className={`input-label input-label-phone ${className}`}
			htmlFor="phone"
			data-testid="phone-input-label"
		>
			<span className="input-placeholder phone-email-label">{label}</span>
			<span className="input-placeholder phone-only-label">
				{phoneLabel}
			</span>
			<CountryCode state={postFields} />
			<input
				type="tel"
				id={`${id}-input`}
				name={id}
				autoComplete="tel"
				inputMode="email"
				className={`form-input bordered phone-input ${inputClassName}`}
				data-testid="phone-input"
				value={postFields[id] || ""}
				onBlur={() => {
					if (validate) validate();
				}}
				onFocus={() => {
					setFocus({ ...focus, [realName]: true });
				}}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					let v = e.target.value;
					v = v.replace(/\s/g, "");
					// The flag mirrors the typed dial code; codes are 1-4 digits, so
					// match longest-first (e.g. +1264 Anguilla before +1 US). Bare
					// digits mean a US-local number.
					let countryCode = postFields.countryCode;
					if (v[0] === "+") {
						const digits = v.slice(1).replace(/\D/g, "");
						for (let len = Math.min(4, digits.length); len >= 1; len--) {
							const prefix = digits.slice(0, len);
							const matches = COUNTRIES.filter(
								(c) => String(c.phone) === prefix,
							);
							if (matches.length) {
								countryCode =
									matches.find((c) => c.code === DIAL_CODE_PRIMARY[prefix]) ??
									matches[0];
								break;
							}
						}
					} else {
						countryCode = COUNTRIES[0];
					}
					setState({
						...postFields,
						countryCode,
						[realName]: v,
					});
				}}
			/>
			<div className="valid-check" />
			{errorMessages}
		</label>
	);
};

export const ErrorMessage = ({ cls, msg }) => {
	return <div className={`error-message ${cls}`}>{msg}</div>;
};
